import React from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';

// Map sideways emoticons to Unicode emojis (same as LinkifyTypography)
const emoticonMap = {
    ':)': '😊',
    ':-)': '😊',
    ':(': '😢',
    ':-(': '😢',
    ':D': '😃',
    ':-D': '😃',
    '<3': '❤️',
    ':P': '😛',
    ':-P': '😛',
    ';)': '😉',
    ';-)': '😉',
    ':O': '😮',
    ':-O': '😮',
    ':|': '😐',
    ':-|': '😐',
    ':/': '😕',
    ':-/': '😕',
    ':\\': '😕',
    ':-\\': '😕',
    ':*': '😘',
    ':-*': '😘',
    '>:(': '😠',
    '>:-(': '😠',
    ':\'(': '😭',
    ':\'-(': '😭',
    'XD': '😆',
    'xD': '😆',
    'xP': '😝',
    'XP': '😝',
};

// Custom keyboard key component
const Kbd = ({ children, className }) => (
    <kbd
        className={cn(
            "inline-flex items-center justify-center rounded-md border border-border bg-muted px-2 py-1",
            "font-mono text-sm font-semibold text-foreground",
            "shadow-[0_1px_0_1px_rgba(0,0,0,0.05)]",
            "min-w-[28px] h-7",
            className
        )}
    >
        {children}
    </kbd>
);

const KeyboardShortcutsModal = ({ open, onClose }) => {
    const shortcuts = [
        {
            category: 'Navigation',
            items: [
                { keys: ['↑'], description: 'Move focus up (items → Doc → Sheet → Folder buttons)' },
                { keys: ['↓'], description: 'Move focus down (Folder → Sheet → Doc → items)' },
                { keys: ['←'], description: 'Move focus to previous child' },
                { keys: ['→'], description: 'Move focus to next child (focuses Folder button if no items)' },
                { keys: ['Enter'], description: 'Open detail pane for item / Activate focused button' },
            ]
        },
        {
            category: 'Selection',
            items: [
                { keys: ['Ctrl', 'Click'], description: 'Multi-select individual items', mac: ['⌘', 'Click'] },
                { keys: ['Ctrl', 'Shift', 'Click'], description: 'Range select items', mac: ['⌘', 'Shift', 'Click'] },
                { keys: ['Esc'], description: 'Clear selection' },
            ]
        },
        {
            category: 'Assignment',
            items: [
                { keys: ['Ctrl', 'A'], description: 'Bulk assign selected items to users (requires 1+ items)', mac: ['⌘', 'A'] },
            ]
        },
        {
            category: 'Item Actions',
            items: [
                { keys: ['Ctrl', 'C'], description: 'Copy selected items (creates shortcuts)', mac: ['⌘', 'C'] },
                { keys: ['Ctrl', 'X'], description: 'Cut selected items (for moving)', mac: ['⌘', 'X'] },
                { keys: ['Ctrl', 'V'], description: 'Paste items to current folder', mac: ['⌘', 'V'] },
                { keys: ['Ctrl', 'Q'], description: 'Toggle star level (0→1→2→3→0)', mac: ['⌘', 'Q'] },
                { keys: ['Alt', 'A'], description: 'Archive/Unarchive focused item or selected items', mac: ['⌥', 'A'] },
            ]
        },
        {
            category: 'Markdown',
            items: [
                { keys: ['# text'], description: 'Heading 1', example: '# Heading', rendered: 'Heading', style: 'text-xl font-bold' },
                { keys: ['## text'], description: 'Heading 2', example: '## Heading', rendered: 'Heading', style: 'text-lg font-bold' },
                { keys: ['### text'], description: 'Heading 3', example: '### Heading', rendered: 'Heading', style: 'text-base font-semibold' },
                { keys: ['**text**'], description: 'Bold', example: '**bold**', rendered: 'bold', style: 'font-semibold' },
                { keys: ['*text*'], description: 'Italic', example: '*italic*', rendered: 'italic', style: 'italic' },
                { keys: ['`code`'], description: 'Inline code', example: '`code`', rendered: 'code', style: 'code' },
                { keys: ['~~text~~'], description: 'Strikethrough', example: '~~deleted~~', rendered: 'deleted', style: 'line-through opacity-70' },
                { keys: ['1. item'], description: 'Numbered list', example: '1. First\n2. Second', rendered: '1. List item', style: '' },
                { keys: ['- item'], description: 'Bullet list', example: '- Item\n- Item', rendered: '• List item', style: '' },
            ]
        },
        {
            category: 'Emoticons',
            items: [
                { keys: [':)'], description: ':)', isEmoji: true },
                { keys: [':-)'], description: ':-)', isEmoji: true },
                { keys: [':('], description: ':(', isEmoji: true },
                { keys: [':-('], description: ':-(', isEmoji: true },
                { keys: [':D'], description: ':D', isEmoji: true },
                { keys: [':-D'], description: ':-D', isEmoji: true },
                { keys: [';)'], description: ';)', isEmoji: true },
                { keys: [';-)'], description: ';-)', isEmoji: true },
                { keys: [':P'], description: ':P', isEmoji: true },
                { keys: [':-P'], description: ':-P', isEmoji: true },
                { keys: [':/'], description: ':/', isEmoji: true },
                { keys: [':-/'], description: ':-/', isEmoji: true },
                { keys: [':|'], description: ':|', isEmoji: true },
                { keys: [':-|'], description: ':-|', isEmoji: true },
                { keys: [':O'], description: ':O', isEmoji: true },
                { keys: [':-O'], description: ':-O', isEmoji: true },
                { keys: [':*'], description: ':*', isEmoji: true },
                { keys: [':-*'], description: ':-*', isEmoji: true },
                { keys: ['>:('], description: '>:(', isEmoji: true },
                { keys: ['>:-('], description: '>:-(', isEmoji: true },
                { keys: ['<3'], description: '<3', isEmoji: true },
            ]
        }
    ];

    // Detect if user is on Mac
    const isMac = typeof navigator !== 'undefined' && navigator.platform.toUpperCase().indexOf('MAC') >= 0;

    const renderKeys = (keys, macKeys) => {
        const keysToShow = isMac && macKeys ? macKeys : keys;
        return (
            <div className="flex items-center gap-1 flex-wrap">
                {keysToShow.map((key, index) => (
                    <React.Fragment key={index}>
                        <Kbd className={key.length > 1 ? 'min-w-[52px]' : ''}>
                            {key}
                        </Kbd>
                        {index < keysToShow.length - 1 && (
                            <span className="text-muted-foreground font-bold text-xs">+</span>
                        )}
                    </React.Fragment>
                ))}
            </div>
        );
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-2xl max-h-[85vh] p-0 gap-0 overflow-hidden">
                <DialogHeader className="px-6 pt-6 pb-4 border-b border-border">
                    <DialogTitle className="text-xl font-semibold">
                        Keyboard Shortcuts
                    </DialogTitle>
                </DialogHeader>

                <ScrollArea className="flex-1 max-h-[calc(85vh-120px)]">
                    <div className="px-6 py-4 space-y-6">
                        {shortcuts.map((section, idx) => (
                            <div key={idx}>
                                <h3 className="text-sm font-semibold text-primary mb-3 uppercase tracking-wide">
                                    {section.category}
                                </h3>

                                {section.category === 'Markdown' ? (
                                    <div className="grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-2">
                                        {section.items.map((shortcut, itemIdx) => (
                                            <div
                                                key={itemIdx}
                                                className={cn(
                                                    "flex flex-col py-3 px-3",
                                                    "rounded-lg border border-border bg-card",
                                                    "transition-colors hover:bg-accent hover:border-accent-foreground/20"
                                                )}
                                            >
                                                <span className="text-xs text-muted-foreground font-mono mb-1.5">
                                                    {shortcut.example}
                                                </span>
                                                <span className={cn(
                                                    "text-sm",
                                                    shortcut.style === 'code'
                                                        ? "px-1.5 py-0.5 bg-muted rounded font-mono text-xs w-fit"
                                                        : shortcut.style
                                                )}>
                                                    {shortcut.rendered}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                ) : section.category === 'Emoticons' ? (
                                    <div className="grid grid-cols-[repeat(auto-fill,minmax(120px,1fr))] gap-2">
                                        {section.items.map((shortcut, itemIdx) => (
                                            <div
                                                key={itemIdx}
                                                className={cn(
                                                    "flex flex-col items-center justify-center py-3 px-2",
                                                    "rounded-lg border border-border bg-card",
                                                    "transition-colors hover:bg-accent hover:border-accent-foreground/20"
                                                )}
                                            >
                                                <span className="text-3xl mb-1">
                                                    {emoticonMap[shortcut.keys[0]] || shortcut.description}
                                                </span>
                                                <span className="text-xs text-muted-foreground font-mono">
                                                    {shortcut.keys[0]}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="space-y-1">
                                        {section.items.map((shortcut, itemIdx) => (
                                            <div
                                                key={itemIdx}
                                                className={cn(
                                                    "flex items-center justify-between py-2.5 px-3",
                                                    "rounded-lg transition-colors hover:bg-accent"
                                                )}
                                            >
                                                <span className="text-sm text-foreground flex-1 mr-4">
                                                    {shortcut.description}
                                                </span>
                                                {renderKeys(shortcut.keys, shortcut.mac)}
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {idx < shortcuts.length - 1 && (
                                    <Separator className="mt-6" />
                                )}
                            </div>
                        ))}

                        <div className="mt-4 p-4 bg-muted rounded-lg">
                            <p className="text-sm text-muted-foreground">
                                <span className="font-semibold">Note:</span> Keyboard navigation must be active for arrow key navigation to work.
                                Navigation is automatically disabled when modals or dialogs are open.
                            </p>
                        </div>
                    </div>
                </ScrollArea>
            </DialogContent>
        </Dialog>
    );
};

export default KeyboardShortcutsModal;
